import React, { useState } from 'react';
import { X, FileText, CheckCircle, XCircle, Trash2, Calendar, DollarSign, RefreshCw, Pencil, Save } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import { formatDate } from '../../utils/dateUtils';

const ViewStatementsModal = ({
  isOpen,
  onClose,
  statements,
  loading,
  onMarkPaid,
  onMarkUnpaid,
  onUpdate,
  onDelete,
  onRecalculateDueDates,
  hasPaymentDueDay = false
}) => {
  const [recalculating, setRecalculating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (statement) => {
    setEditingId(statement.id);
    setEditForm({
      due_date: statement.due_date || '',
      minimum_payment: statement.minimum_payment ?? '',
      statement_balance: statement.statement_balance ?? '',
      notes: statement.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (statementId) => {
    if (!onUpdate || !editForm.due_date) return;
    setSaving(true);
    try {
      const updates = {
        due_date: editForm.due_date,
        minimum_payment: editForm.minimum_payment === '' ? null : Number(editForm.minimum_payment),
        notes: editForm.notes || null,
      };
      // statement_balance is NOT NULL — only send it when a valid number is present.
      if (editForm.statement_balance !== '' && !Number.isNaN(Number(editForm.statement_balance))) {
        updates.statement_balance = Number(editForm.statement_balance);
      }
      await onUpdate(statementId, updates);
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    if (!onRecalculateDueDates) return;
    setRecalculating(true);
    try {
      await onRecalculateDueDates();
    } finally {
      setRecalculating(false);
    }
  };
  const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      showCloseButton={false}
      closeOnBackdrop={true}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">View Statements</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 dark:border-green-400"></div>
          </div>
        ) : statements.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No statements found</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              Add your first statement to start tracking
            </p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {statements.map((statement) => (
              <div
                key={statement.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Statement #{statement.id}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {statement.transaction_count || 0} transactions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {statement.is_paid ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Paid
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
                        <XCircle className="h-3 w-3 mr-1" />
                        Unpaid
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Statement Date</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                      {formatDate(statement.statement_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Closing Date</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                      {formatDate(statement.closing_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Due Date</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                      <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                      {formatDate(statement.due_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Balance</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white flex items-center">
                      <DollarSign className="h-3 w-3 mr-1 text-gray-400" />
                      {formatCurrency(statement.statement_balance)}
                    </p>
                  </div>
                </div>

                {statement.minimum_payment && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Minimum Payment: {formatCurrency(statement.minimum_payment)}
                    </p>
                  </div>
                )}

                {statement.notes && editingId !== statement.id && (
                  <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-600 dark:text-gray-300">
                    {statement.notes}
                  </div>
                )}

                {editingId === statement.id && (
                  <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={editForm.due_date}
                          onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min Payment</label>
                        <input
                          type="number" step="0.01" min="0"
                          value={editForm.minimum_payment}
                          onChange={(e) => setEditForm({ ...editForm, minimum_payment: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Balance</label>
                        <input
                          type="number" step="0.01"
                          value={editForm.statement_balance}
                          onChange={(e) => setEditForm({ ...editForm, statement_balance: e.target.value })}
                          className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</label>
                      <input
                        type="text"
                        value={editForm.notes}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  {editingId === statement.id ? (
                    <>
                      <button
                        onClick={() => saveEdit(statement.id)}
                        disabled={saving || !editForm.due_date}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      {onUpdate && (
                        <button
                          onClick={() => startEdit(statement)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                      )}
                      {statement.is_paid ? (
                        <button
                          onClick={() => onMarkUnpaid(statement.id)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-yellow-700 dark:text-yellow-400 bg-white dark:bg-gray-700 border border-yellow-300 dark:border-yellow-600 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Mark Unpaid
                        </button>
                      ) : (
                        <button
                          onClick={() => onMarkPaid(statement.id)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Mark Paid
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(statement.id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
        <div>
          {hasPaymentDueDay && statements.length > 0 && (
            <button
              type="button"
              onClick={handleRecalculate}
              disabled={recalculating}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'Recalculating...' : 'Recalculate Due Dates'}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
        >
          Close
        </button>
      </div>
    </BaseModal>
  );
};

export default ViewStatementsModal;
